import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useDocsPosts, useDocsCategories } from '@/hooks/useWordPressDocs';
import { docsService } from '@/services/docsService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';

const SupportCategory = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const { data: categories } = useDocsCategories();
  const category = categories?.find(cat => cat.slug === slug);
  
  const { data: postsData, isLoading } = useDocsPosts({
    doc_category: category?.id?.toString(),
    per_page: 20
  });

  const estimateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = docsService.stripHtml(content).split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handlePostClick = (post: any) => {
    navigate(`/support/article/${post.slug}`);
  };

  if (!category && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Category Not Found</h1>
          <Link to="/support" className="text-primary hover:underline">
            ← Back to Support
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${category?.name || 'Category'} - Support - SeniorThrive`}
        description={category?.description || `Support resources for ${category?.name}`}
      />
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-muted/30 py-12">
          <div className="container mx-auto px-4">
            <Link 
              to="/support" 
              className="inline-flex items-center text-primary hover:underline mb-6"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Support Center
            </Link>
            
            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {category?.name || 'Loading...'}
              </h1>
              {category?.description && (
                <p className="text-lg text-muted-foreground">
                  {category.description}
                </p>
              )}
              <div className="mt-4">
                <Badge variant="secondary">
                  {postsData?.total || 0} Resources
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-6 bg-muted rounded w-full"></div>
                    <div className="h-4 bg-muted rounded w-5/6"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded w-full"></div>
                      <div className="h-3 bg-muted rounded w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : postsData?.posts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No Resources Found</h3>
              <p className="text-muted-foreground mb-6">
                We're working on adding resources to this category.
              </p>
              <Link 
                to="/support" 
                className="text-primary hover:underline"
              >
                Browse other categories
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {postsData?.posts.map((post) => {
                const readingTime = estimateReadingTime(post.content.rendered);
                const publishDate = new Date(post.date).toLocaleDateString();
                
                return (
                  <Card 
                    key={post.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => handlePostClick(post)}
                  >
                    <CardHeader>
                      <CardTitle className="group-hover:text-primary transition-colors">
                        {docsService.stripHtml(post.title.rendered)}
                      </CardTitle>
                      <CardDescription>
                        {docsService.stripHtml(post.excerpt.rendered).substring(0, 150)}...
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {readingTime}
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {publishDate}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SupportCategory;